import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Badge, Button, Card, Group, Skeleton, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, MetricCard, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import type { Indexed } from '../../../../lib/format';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { statusTone } from '../../../../lib/status-tone';
import { useT } from '../../../../i18n/I18nProvider';
import { HttpCallDrawer } from '../../calls/HttpCallDrawer';

type FlowRun = flows.FlowRun;

const PAGE_SIZE = 25;

function duration(startedAt: string, finishedAt: string | null | undefined): string {
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const ms = Math.max(0, end - start);
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
  return `${Math.floor(ms / 3_600_000)}h ${Math.floor((ms % 3_600_000) / 60_000)}m`;
}

/**
 * What the flow actually did.
 *
 * `flows-view-id.md` on this tab:
 * - §4 item 1: the Error column rendered `error_code`, which the projection
 *   never returned, so the one failure-diagnosis affordance on the page was a
 *   permanently empty column. `services/flows-llm` now projects `error`, which
 *   was on the entity all along;
 * - §4 item 3: no error state, so a 500 looked like a healthy flow with no
 *   traffic;
 * - §4 item 5: `trigger_text`, `final_reply`, `confidence`, `model` and
 *   `citations` were all returned and all dropped. That is the difference
 *   between "the flow ran" and "the flow worked", so a row now opens them;
 * - §4 items 6, 7 and 10: no paging, no "failed only", no aggregate.
 */
export function RunsTab({ flowId, compact }: { flowId: string; compact?: boolean }) {
  const t = useT();
  const [failedOnly, setFailedOnly] = useState(false);
  // The Overview tab shows the five most recent runs beside the lifecycle
  // card; the Runs tab shows the full, paged, filterable table.
  const pageSize = compact ? 5 : PAGE_SIZE;
  const [page, setPage] = useState(1);
  const [inspecting, setInspecting] = useState<FlowRun | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['flow', flowId, 'runs', failedOnly, page, pageSize],
    queryFn: () => flows.listFlowRuns(flowId, {
      limit: pageSize,
      offset: (page - 1) * pageSize,
      ...(failedOnly ? { status: 'failed' } : {})
    }),
    placeholderData: (prev) => prev,
    // No flows hub exists (nothing maps one, and `registerHub` is not
    // exported), so a poll is the only liveness available here.
    refetchInterval: 10_000
  });

  useEffect(() => { setPage(1); }, [failedOnly]);

  const runs = (data?.data ?? []) as Indexed<FlowRun>[];
  const total = data?.meta?.total ?? 0;

  // §4 item 10: the runs are already in hand, so this is free.
  const stats = useMemo(() => {
    const failed = runs.filter((r) => r.status === 'failed').length;
    const finished = runs.filter((r) => r.finished_at);
    const durations = finished
      .map((r) => new Date(r.finished_at!).getTime() - new Date(r.started_at).getTime())
      .sort((a, b) => a - b);
    const median = durations.length > 0 ? durations[Math.floor(durations.length / 2)] : null;
    return { failed, median };
  }, [runs]);

  return (
    <Stack gap="sm">
      {!compact && (
      <Group gap="sm" wrap="wrap">
        <MetricCard label={t('flows.view.runs.total')} value={total} />
        <MetricCard label={t('status.failed')} value={stats.failed} />
        <MetricCard
          label={t('flows.view.runs.median')}
          value={stats.median === null ? '–' : duration(new Date(0).toISOString(), new Date(stats.median).toISOString())}
        />
      </Group>
      )}

      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="center" wrap="wrap">
            <Title order={4}>{t('flows.view.runs.title')}</Title>
            <Group gap="xs">
              {compact ? (
                <a className="rr-flows__viewall" href={`/flows/${flowId}?tab=runs`}>
                  {t('flows.view.runs.viewAll')}
                </a>
              ) : (
                <>
                  <Button
                    size="xs"
                    variant={failedOnly ? 'filled' : 'default'}
                    onClick={() => setFailedOnly((v) => !v)}
                  >
                    {t('flows.view.runs.failedOnly')}
                  </Button>
                  <Text fz="xs" c="var(--rr-color-text-muted)">{t('flows.view.runs.liveHint')}</Text>
                </>
              )}
            </Group>
          </Group>

          {error && <Alert color="error" role="alert">{t('flows.view.runs.error')}</Alert>}

          {isPending && <Stack gap="xs">{[0, 1, 2].map((i) => <Skeleton key={i} height={36} />)}</Stack>}

          {!isPending && !error && runs.length === 0 && (
            <EmptyState
              title={failedOnly ? t('flows.view.runs.noFailures') : t('flows.runs.empty')}
              description={failedOnly ? undefined : t('flows.view.runs.emptyHint')}
              action={failedOnly ? <Button size="xs" variant="default" onClick={() => setFailedOnly(false)}>{t('inbox.filters.clear')}</Button> : undefined}
            />
          )}

          {!isPending && !error && runs.length > 0 && (
            <DataTable<Indexed<FlowRun>>
              records={runs}
              idAccessor="id"
              onRowClick={setInspecting}
              page={page}
              totalRecords={compact ? runs.length : total}
              recordsPerPage={pageSize}
              onPageChange={setPage}
              emptyState={t('flows.runs.empty')}
              columns={[
                {
                  accessor: 'status',
                  title: t('common.status'),
                  render: (r) => (
                    <StatusBadge status={statusTone(r.status)}>
                      {t(`flows.runStatus.${r.status}`, r.status)}
                    </StatusBadge>
                  )
                },
                {
                  accessor: 'contact_msisdn',
                  title: t('contacts.columns.msisdn'),
                  render: (r) => r.contact_msisdn
                    ? <code>{r.contact_msisdn}</code>
                    : <Text fz="xs" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
                },
                {
                  accessor: 'trigger_text',
                  title: t('flows.view.runs.trigger'),
                  // What the customer said, which is the only way to tell one
                  // run from another at a glance.
                  render: (r) => <span className="rr-flows__run-text">{r.trigger_text ?? '–'}</span>
                },
                {
                  accessor: 'error',
                  title: t('flows.view.runs.reason'),
                  render: (r) => r.error
                    ? (
                      <Tooltip label={r.error} position="top">
                        <span className="rr-flows__run-error">{r.error}</span>
                      </Tooltip>
                    )
                    : <Text fz="xs" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
                },
                {
                  accessor: 'started_at',
                  title: t('flows.view.runs.started'),
                  render: (r) => (
                    <Tooltip label={formatDateTime(r.started_at)} position="top">
                      <span>{formatRelative(r.started_at)}</span>
                    </Tooltip>
                  )
                },
                {
                  accessor: 'finished_at',
                  title: t('flows.view.runs.duration'),
                  render: (r) => duration(r.started_at, r.finished_at)
                }
              ]}
            />
          )}
        </Stack>
      </Card>

      <RunDrawer run={inspecting} onClose={() => setInspecting(null)} />
    </Stack>
  );
}

/** The conversation turn, and what the model grounded its answer on. */
function RunDrawer({ run, onClose }: { run: FlowRun | null; onClose: () => void }) {
  const [call, setCall] = useState<string | null>(null);
  const t = useT();

  return (
    <>
    <Drawer opened={!!run} onClose={onClose} title={t('flows.view.runs.detail')} position="right" size="md">
      {run && (
        <Stack gap="md" style={{ padding: 4 }}>
          <Group gap="xs" wrap="wrap">
            <StatusBadge status={statusTone(run.status)}>
              {t(`flows.runStatus.${run.status}`, run.status)}
            </StatusBadge>
            {run.model && <Badge size="xs" variant="light">{run.model}</Badge>}
            {run.confidence !== null && run.confidence !== undefined && (
              <Badge size="xs" variant="light">
                {t('flows.view.runs.confidence', { value: (run.confidence * 100).toFixed(0) })}
              </Badge>
            )}
          </Group>

          {run.error && <Alert color="error" role="alert">{run.error}</Alert>}

          {/* A run that is a conversation stands on a node while it waits. */}
          {run.state && run.state !== 'done' && (
            <Alert color="info">
              {t('flows.view.runs.waiting', { state: t(`flows.test.states.${run.state}`, run.state), node: run.current_node_id ?? '–' })}
            </Alert>
          )}

          <Stack gap={4}>
            <Text role="inputLabel">{t('flows.view.runs.trigger')}</Text>
            <Card withBorder><Text role="bodySmall">{run.trigger_text ?? t('common.none')}</Text></Card>
            {/* Why this run began: a keyword, being asked for, a tapped button. */}
            {run.started_by && (
              <Text role="caption" c="var(--rr-color-text-muted)" data-testid="run-started-by">
                {t(`flows.view.runs.startedBy.${run.started_by}`, { score: Math.round((run.intent_score ?? 0) * 100) })}
              </Text>
            )}
          </Stack>

          <Stack gap={4}>
            <Text role="inputLabel">{t('flows.view.runs.reply')}</Text>
            <Card withBorder><Text role="bodySmall" style={{ whiteSpace: 'pre-wrap' }}>{run.final_reply ?? t('common.none')}</Text></Card>
          </Stack>

          {(run.citations?.length ?? 0) > 0 && (
            <Stack gap={4}>
              <Text role="inputLabel">{t('flows.view.runs.citations')}</Text>
              {/* What the answer was grounded on: the difference between "the
                  model said something" and "the model said something the
                  knowledge base supports". */}
              {run.citations!.map((c, i) => (
                <Card key={i} withBorder>
                  <Group justify="space-between">
                    <Text fz="xs">{t('flows.view.runs.citation', { chunk: c.chunk_index })}</Text>
                    <Badge size="xs" variant="light">{(c.score * 100).toFixed(0)}%</Badge>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}

          {(run.steps?.length ?? 0) > 0 && (
            <Stack gap={4}>
              <Text role="inputLabel">{t('flows.view.runs.steps')}</Text>
              {/* The path the conversation took, node by node: which branch a
                  condition chose, what an ask collected, where an HTTP call
                  failed. This is the difference between "the flow ran" and
                  "the flow did what was meant". */}
              <ol className="rr-flows__steps">
                {run.steps!.map((s, i) => (
                  <li key={i} className="rr-flows__step">
                    <span className="rr-flows__step-node">{s.name || s.node}</span>
                    <span className="rr-flows__step-outcome" data-outcome={s.outcome}>{s.outcome}</span>
                    {s.detail ? <span className="rr-flows__step-detail">{s.detail}</span> : null}
                    {/* The API call this step made, kept 48 hours (19 October 2026). */}
                    {(s as flows.FlowStep).call ? (
                      <Button size="xs" variant="subtle" onClick={() => setCall((s as flows.FlowStep).call ?? null)} data-testid="run-step-call">{t('flows.calls.open')}</Button>
                    ) : null}
                  </li>
                ))}
              </ol>
            </Stack>
          )}

          {typeof run.variables === 'object' && run.variables !== null && Object.keys(run.variables as Record<string, unknown>).length > 0 ? (
            <details className="rr-flows__vars">
              <summary>{t('flows.view.runs.variables')}</summary>
              <pre className="rr-flows__pre">{JSON.stringify(run.variables, null, 2)}</pre>
            </details>
          ) : null}

          <Stack gap={4}>
            <Text role="inputLabel">{t('flows.view.runs.timing')}</Text>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {formatDateTime(run.started_at)}
              {run.finished_at ? ` · ${duration(run.started_at, run.finished_at)}` : ` · ${t('flows.view.runs.stillRunning')}`}
            </Text>
          </Stack>
        </Stack>
      )}
    </Drawer>
    <HttpCallDrawer callId={call} onClose={() => setCall(null)} />
    </>
  );
}
